import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  Sparkles,
  ShieldCheck,
  Lock,
  Truck,
  RefreshCcw,
  Mail,
  Clock,
  Flame,
} from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/Header";
import SearchBar from "@/components/SearchBar";
import CategoryChips from "@/components/CategoryChips";
import ProductCard from "@/components/ProductCard";
import HomeHero from "@/components/HomeHero";
import Footer from "@/components/Footer";
import CountdownTimer from "@/components/CountdownTimer";
// Below-the-fold rail — deferred so its JS + its "recently viewed products"
// state hydration does not block the initial paint of the hero + top rails.
const RecentlyViewed = lazy(() => import("@/components/RecentlyViewed"));
import { api } from "@/lib/api";
import { useSeo, organizationJsonLd, SITE_URL } from "@/lib/useSeo";
import { LuxProductRailSkeleton } from "@/components/LuxLoader";

// ---------------- Reusable helpers (Home-only) ----------------

const HRow = ({ testId, children }) => {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (!ref.current) return;
    ref.current.scrollBy({ left: dir * 280, behavior: "smooth" });
  };
  return (
    <div className="relative -mx-4 px-4 sm:mx-0 sm:px-0">
      <div
        ref={ref}
        data-testid={testId}
        className="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2"
      >
        {children}
      </div>
      <button
        onClick={() => scroll(-1)}
        className="absolute -left-2 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white p-2 shadow-lg md:flex"
        aria-label="Scroll left"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        onClick={() => scroll(1)}
        className="absolute -right-2 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white p-2 shadow-lg md:flex"
        aria-label="Scroll right"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const SectionHead = ({ eyebrow, title, to, viewLabel = "View all", eyebrowColor = "gray-500" }) => (
  <div className="mb-7 flex items-end justify-between">
    <div>
      <span
        className={`text-[10px] uppercase tracking-[0.3em] ${
          eyebrowColor === "gold" ? "text-[#D4AF37]" : eyebrowColor === "gold-dark" ? "text-[#B8912B]" : "text-gray-500"
        }`}
      >
        {eyebrow}
      </span>
      <h2 className="mt-1 font-heading text-2xl sm:text-3xl">{title}</h2>
    </div>
    {to && (
      <Link to={to} className="text-xs uppercase tracking-widest text-black hover:underline">
        {viewLabel}
      </Link>
    )}
  </div>
);

const CardWrap = ({ children }) => (
  <div className="w-[168px] shrink-0 snap-start sm:w-[210px]">{children}</div>
);

// ---------------- Data ----------------

import { CATEGORIES as ALL_CATEGORIES } from "@/lib/categories";

// V1.0 category rail on the home page — shows the full 15-category catalogue
// as a 3-across (mobile) / 5-across (desktop) grid without redesigning the tile.
const CATEGORIES = ALL_CATEGORIES;

const STYLES = [
  {
    key: "casual",
    label: "Casual Everyday",
    subtitle: "Effortless & easy",
    to: "/category/t-shirts",
    image: "https://images.unsplash.com/photo-1516823975973-ca61ffe450c0?crop=entropy&cs=srgb&fm=jpg&q=90&w=900",
  },
  {
    key: "office",
    label: "Office Elegance",
    subtitle: "Polished silhouettes",
    to: "/category/shirts",
    image: "https://images.unsplash.com/photo-1589810635657-232948472d98?crop=entropy&cs=srgb&fm=jpg&q=90&w=900",
  },
  {
    key: "evening",
    label: "Evening Grace",
    subtitle: "For the golden hour",
    to: "/collection/new",
    image: "https://images.unsplash.com/photo-1590588503756-08a4b2be5eb9?crop=entropy&cs=srgb&fm=jpg&q=90&w=900",
  },
  {
    key: "weekend",
    label: "Weekend Denim",
    subtitle: "Off-duty energy",
    to: "/category/jeans",
    image: "https://images.unsplash.com/photo-1475178626620-a4d074967452?crop=entropy&cs=srgb&fm=jpg&q=90&w=900",
  },
];

const PREMIUM_COLLECTIONS = [
  {
    key: "silk",
    tag: "The Silk Story",
    title: "Softness, distilled",
    subtitle: "Silk-touch shirts, effortlessly draped.",
    to: "/category/shirts",
    image: "https://images.unsplash.com/photo-1760551937420-f34b764b1256?crop=entropy&cs=srgb&fm=jpg&q=95&w=1400",
    accent: "gold",
  },
  {
    key: "denim",
    tag: "The Denim Edit",
    title: "Indigo, refined",
    subtitle: "Cut from Turkish mills, softened for you.",
    to: "/category/jeans",
    image: "https://images.unsplash.com/photo-1715758890151-2c15d5d482aa?crop=entropy&cs=srgb&fm=jpg&q=95&w=1400",
    accent: "gold-dark",
  },
];

const WHY = [
  { icon: ShieldCheck, title: "100% Quality", subtitle: "Handpicked fabrics, checked twice" },
  { icon: Lock, title: "Secure Payments", subtitle: "256-bit encrypted checkout" },
  { icon: Truck, title: "Fast Delivery", subtitle: "Ships in 24h, pan-India" },
  { icon: RefreshCcw, title: "Easy Returns", subtitle: "7-Day Return & Exchange" },
];

// ---------------- Page ----------------

const Home = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/products")
      .then(({ data }) => {
        if (!cancelled) setAll(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const trending = [...all].sort((a, b) => (b.discount_percent || 0) - (a.discount_percent || 0)).slice(0, 8);
  const bestSellers = [...all].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0)).slice(0, 8);
  const newArrivals = [...all].slice(-8).reverse();
  const editors = [...all].filter((p) => p.is_featured).slice(0, 6);
  const limited = [...all]
    .filter((p) => (p.discount_percent || 0) >= 20)
    .slice(0, 6);
  const flashSale = [...all]
    .filter((p) => (p.discount_percent || 0) >= 20)
    .sort((a, b) => (b.discount_percent || 0) - (a.discount_percent || 0))
    .slice(0, 6);

  // Countdown targets — stable across renders. Flash Sale ends at midnight;
  // Limited Edition rolls over every 3 days from a fixed anchor.
  const endOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);
  const limitedEditionEnd = useMemo(() => {
    const anchor = new Date("2026-01-01T00:00:00Z").getTime();
    const cycle = 3 * 24 * 60 * 60 * 1000; // 3 days
    const next = anchor + Math.ceil((Date.now() - anchor) / cycle) * cycle;
    return new Date(next);
  }, []);

  const [email, setEmail] = useState("");
  const submitEmail = (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    toast.success("Welcome to the House — 10% off is on its way!");
    setEmail("");
  };

  useSeo({
    title: "Royal Lady — Luxury Fashion for the Modern Woman",
    description:
      "Discover timeless sarees, kurtis, dress materials and jewellery, handcrafted in India. Free shipping over ₹999 · 7-day easy returns · Cash on Delivery available.",
    canonical: `${SITE_URL}/`,
    jsonLd: organizationJsonLd,
  });

  return (
    <div className="bg-white" data-testid="home-page">
      <Header />

      {/* Search directly below header */}
      <div className="mx-auto max-w-screen-md px-4 pt-3 sm:px-6" data-testid="home-search-section">
        <SearchBar />
      </div>

      {/* Category chips */}
      <div className="mx-auto mt-3 max-w-screen-md px-4 sm:px-6">
        <CategoryChips />
      </div>

      {/* Offer strip */}
      <div
        data-testid="offer-strip"
        className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-black px-4 py-2 text-[11px] uppercase tracking-widest text-white"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-3 w-3 text-[#D4AF37]" />
          <span>
            Free shipping ₹999+ · Code <span className="text-[#D4AF37]">ROYAL10</span> for 10% off
          </span>
        </div>
        <span className="hidden h-1 w-1 rounded-full bg-white/30 sm:inline" />
        <div className="flex items-center gap-2">
          <span className="text-white/60">Offer ends in</span>
          <CountdownTimer
            endTime={endOfToday}
            tone="dark"
            size="sm"
            showLabels={false}
            testId="offer-strip-countdown"
          />
        </div>
      </div>

      {/* Hero carousel */}
      <HomeHero products={all} />

      {/* Categories */}
      <section className="mx-auto max-w-screen-lg px-4 py-14 sm:px-6" data-testid="categories-section">
        <SectionHead eyebrow="Curated" title="Shop by category" to="/category/t-shirts" viewLabel="All" />
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 sm:gap-6">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              data-testid={`category-tile-${c.key}`}
              className="group relative block overflow-hidden rounded-xl bg-gray-100"
            >
              <div className="aspect-[3/4] w-full overflow-hidden">
                <img
                  src={c.image}
                  alt={c.label}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent px-3 py-4">
                <span className="font-heading text-lg text-white sm:text-xl">{c.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trending Now */}
      {/* Trending Now */}
      {loading ? (
        <section className="mx-auto max-w-screen-lg px-4 py-12 sm:px-6" data-testid="trending-section">
          <SectionHead eyebrow="Editor's pick" title="Trending now" to="/collection/sale" eyebrowColor="gold" />
          <LuxProductRailSkeleton count={6} testId="trending-scroll-skeleton" />
        </section>
      ) : trending.length > 0 && (
        <section className="mx-auto max-w-screen-lg px-4 py-12 sm:px-6" data-testid="trending-section">
          <SectionHead eyebrow="Editor's pick" title="Trending now" to="/collection/sale" eyebrowColor="gold" />
          <HRow testId="trending-scroll">
            {trending.map((p) => (
              <CardWrap key={p.id}>
                <ProductCard product={p} testIdPrefix="trending-product" />
              </CardWrap>
            ))}
          </HRow>
        </section>
      )}

      {/* Flash Sale — luxury dark banner with big countdown */}
      {flashSale.length > 0 && (
        <section className="mx-4 mt-4 overflow-hidden rounded-2xl sm:mx-6" data-testid="flash-sale-section">
          <div className="relative bg-gradient-to-br from-black via-[#0b0b0d] to-black px-5 py-10 text-white sm:px-10 sm:py-12">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-white/5 blur-3xl" />
            <div className="relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">
                  <Flame className="h-3.5 w-3.5" /> Flash Sale · Today only
                </span>
                <h2 className="mt-2 font-heading text-3xl leading-tight sm:text-4xl">
                  Up to <em className="not-italic text-gold-gradient">30% off</em> on House icons
                </h2>
                <p className="mt-2 max-w-md text-sm text-white/60">
                  Handpicked silhouettes at their most tempting price. Ends when the clock strikes twelve.
                </p>
              </div>
              <div className="flex flex-col items-start gap-2 md:items-end">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/60">Ends in</span>
                <CountdownTimer
                  endTime={endOfToday}
                  tone="gold"
                  size="lg"
                  testId="flash-sale-countdown"
                />
              </div>
            </div>

            <div className="relative mt-8">
              <div
                data-testid="flash-sale-scroll"
                className="scrollbar-hide -mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-1 sm:-mx-10 sm:px-10"
              >
                {flashSale.map((p) => (
                  <div key={p.id} className="w-[168px] shrink-0 snap-start sm:w-[200px]">
                    <div className="relative">
                      <span className="pointer-events-none absolute -top-2 left-2 z-10 bg-[#D4AF37] px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-black">
                        Flash
                      </span>
                      <div className="bg-white p-2">
                        <ProductCard product={p} testIdPrefix="flash-sale-product" quickBuy />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/collection/sale"
                data-testid="flash-sale-view-all"
                className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3 text-[11px] font-semibold uppercase tracking-widest text-black transition-colors hover:bg-white"
              >
                Shop the Flash Sale <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Shop by Style — luxury image tiles */}
      <section className="mx-auto max-w-screen-lg px-4 py-14 sm:px-6" data-testid="shop-by-style-section">
        <SectionHead eyebrow="Find your look" title="Shop by style" eyebrowColor="gold" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-5">
          {STYLES.map((s, idx) => (
            <Link
              key={s.key}
              to={s.to}
              data-testid={`style-tile-${s.key}`}
              className="group relative block overflow-hidden rounded-xl reveal"
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div className="aspect-[3/4] w-full overflow-hidden">
                <img
                  src={s.image}
                  alt={s.label}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                <span className="text-[9px] uppercase tracking-[0.35em] text-white/70">{s.subtitle}</span>
                <div className="mt-1 font-heading text-lg sm:text-xl">{s.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* New Arrivals */}
      {loading ? (
        <section className="mx-auto max-w-screen-lg px-4 py-12 sm:px-6" data-testid="new-arrivals-section">
          <SectionHead eyebrow="Just landed" title="New arrivals" to="/collection/new" />
          <LuxProductRailSkeleton count={6} testId="new-arrivals-scroll-skeleton" />
        </section>
      ) : newArrivals.length > 0 && (
        <section className="mx-auto max-w-screen-lg px-4 py-12 sm:px-6" data-testid="new-arrivals-section">
          <SectionHead eyebrow="Just landed" title="New arrivals" to="/collection/new" />
          <HRow testId="new-arrivals-scroll">
            {newArrivals.map((p) => (
              <CardWrap key={p.id}>
                <ProductCard product={p} testIdPrefix="new-arrival-product" />
              </CardWrap>
            ))}
          </HRow>
        </section>
      )}

      {/* Premium Collections */}
      <section className="mx-auto max-w-screen-lg px-4 py-14 sm:px-6" data-testid="premium-collections-section">
        <SectionHead eyebrow="House icons" title="Premium collections" eyebrowColor="gold" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {PREMIUM_COLLECTIONS.map((c) => (
            <Link
              key={c.key}
              to={c.to}
              data-testid={`premium-collection-${c.key}`}
              className="group relative block overflow-hidden rounded-2xl"
            >
              <div className="aspect-[4/5] w-full overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-[800ms] group-hover:scale-[1.06]"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-8">
                <span
                  className={`text-[10px] uppercase tracking-[0.35em] ${
                    c.accent === "gold" ? "text-[#D4AF37]" : "text-[#F4D97A]"
                  }`}
                >
                  {c.tag}
                </span>
                <h3 className="mt-2 font-heading text-2xl sm:text-3xl">{c.title}</h3>
                <p className="mt-1 max-w-xs text-sm text-white/80">{c.subtitle}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white">
                  Discover <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Best Sellers */}
      {loading ? (
        <section className="mx-auto max-w-screen-lg px-4 py-12 sm:px-6" data-testid="best-sellers-section">
          <SectionHead eyebrow="Loved by many" title="Best sellers" to="/category/jeans" />
          <LuxProductRailSkeleton count={6} testId="best-sellers-scroll-skeleton" />
        </section>
      ) : bestSellers.length > 0 && (
        <section className="mx-auto max-w-screen-lg px-4 py-12 sm:px-6" data-testid="best-sellers-section">
          <SectionHead eyebrow="Loved by many" title="Best sellers" to="/category/jeans" />
          <HRow testId="best-sellers-scroll">
            {bestSellers.map((p) => (
              <CardWrap key={p.id}>
                <ProductCard product={p} testIdPrefix="best-seller-product" />
              </CardWrap>
            ))}
          </HRow>
        </section>
      )}

      {/* Limited Edition — dark accent section */}
      {limited.length > 0 && (
        <section className="mt-8 bg-black py-14 text-white" data-testid="limited-edition-section">
          <div className="mx-auto max-w-screen-lg px-4 sm:px-6">
            <div className="mb-8 flex flex-col items-start justify-between gap-5 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  <Clock className="h-3 w-3" /> Only while it lasts
                </span>
                <h2 className="mt-1 font-heading text-2xl text-white sm:text-3xl">Limited edition</h2>
                <p className="mt-1 max-w-md text-sm text-white/60">
                  Small batches. Numbered production. Once they're gone, they're gone.
                </p>
              </div>
              <div className="flex flex-col items-start gap-2 md:items-end">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/60">Restock resets in</span>
                <CountdownTimer
                  endTime={limitedEditionEnd}
                  tone="dark"
                  size="md"
                  testId="limited-edition-countdown"
                />
              </div>
            </div>
            <HRow testId="limited-scroll">
              {limited.map((p) => (
                <CardWrap key={p.id}>
                  <div className="relative">
                    <span className="pointer-events-none absolute -top-2 left-2 z-10 bg-[#D4AF37] px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                      Limited
                    </span>
                    <div className="bg-white p-2">
                      <ProductCard product={p} testIdPrefix="limited-product" quickBuy />
                    </div>
                  </div>
                </CardWrap>
              ))}
            </HRow>
          </div>
        </section>
      )}

      {/* Editor's Picks — magazine layout */}
      {loading ? (
        <section className="mx-auto max-w-screen-lg px-4 py-14 sm:px-6" data-testid="editors-picks-section">
          <SectionHead eyebrow="Handpicked" title="Editor's Choice" eyebrowColor="gold-dark" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="editors-picks-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={i === 0 ? "col-span-2 row-span-2" : ""}>
                <div className="lux-shimmer aspect-[3/4] w-full" aria-hidden />
                <div className="lux-shimmer mt-2 h-3 w-3/4" aria-hidden />
                <div className="lux-shimmer mt-1.5 h-3 w-1/3" aria-hidden />
              </div>
            ))}
          </div>
        </section>
      ) : editors.length > 0 && (
        <section className="mx-auto max-w-screen-lg px-4 py-14 sm:px-6" data-testid="editors-picks-section">
          <SectionHead eyebrow="Handpicked" title="Editor's Choice" eyebrowColor="gold-dark" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {editors.slice(0, 6).map((p, idx) => (
              <div
                key={p.id}
                className={`reveal ${idx === 0 ? "col-span-2 row-span-2" : ""}`}
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <ProductCard product={p} testIdPrefix="editor-pick" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Why Choose Royal Lady */}
      <section className="bg-[#FAFAFA] py-16" data-testid="why-royal-lady-section">
        <div className="mx-auto max-w-screen-lg px-4 sm:px-6">
          <div className="mb-9 text-center">
            <span className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">The Royal Lady promise</span>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl">Why choose Royal Lady?</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
              Every detail — from the fabric to the packaging — is designed to feel like a small, private luxury.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {WHY.map((w, idx) => (
              <div
                key={w.title}
                data-testid={`why-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="reveal group flex flex-col items-center gap-3 border border-gray-100 bg-white p-6 text-center transition-all hover:-translate-y-1 hover:border-[#D4AF37] hover:shadow-lg"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className="grid h-14 w-14 place-items-center bg-[#FBF6E4] transition-colors group-hover:bg-[#D4AF37]">
                  <w.icon className="h-6 w-6 text-[#B8912B] group-hover:text-white" />
                </div>
                <div className="text-xs font-semibold uppercase tracking-widest text-black">{w.title}</div>
                <div className="text-[11px] text-gray-500">{w.subtitle}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews section removed — will be re-enabled when real, verified reviews exist. */}

      {/* The Royal Lady Story — heritage / brand ethos */}
      <section className="relative overflow-hidden bg-white" data-testid="brand-story-section">
        <div className="mx-auto grid max-w-screen-lg items-stretch gap-0 px-0 sm:grid-cols-2">
          {/* Image panel */}
          <div className="relative min-h-[380px] overflow-hidden sm:min-h-[540px]" data-testid="brand-story-image">
            <img
              src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?crop=entropy&cs=srgb&fm=jpg&q=90&w=1400"
              alt="Royal Lady atelier"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-[1200ms] hover:scale-[1.04]"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
            <div className="pointer-events-none absolute left-6 top-6 flex items-center gap-2 border border-white/40 bg-black/30 px-3 py-1 backdrop-blur-md">
              <span className="text-[9px] uppercase tracking-[0.4em] text-white">Est. 2026</span>
            </div>
          </div>

          {/* Copy panel */}
          <div className="flex flex-col justify-center px-6 py-14 sm:px-10 sm:py-16">
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#B8912B]">The House of Royal Lady</span>
            <h2 className="mt-3 font-heading text-3xl leading-tight sm:text-4xl">
              Quiet luxury, <em className="not-italic text-gold-gradient">crafted in India</em>
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-[15px]">
              Royal Lady was born from a simple belief — that everyday clothing can feel like a small, private ceremony. We work with independent Indian ateliers who still cut, stitch and finish by hand, sourcing handloom silks, mulmul cottons and Turkish denim from mills we've visited ourselves.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-[15px]">
              Every piece is quality-checked twice, wrapped in gold-embossed muslin and delivered with a handwritten thank-you. No fast fashion. No shortcuts. Just timeless silhouettes made to be loved for years.
            </p>

            {/* Pillars */}
            <div className="mt-8 grid grid-cols-3 gap-4 border-t border-gray-100 pt-6">
              <div data-testid="brand-story-pillar-handcrafted">
                <div className="font-heading text-2xl text-black">120+</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-gray-500">Master Artisans</div>
              </div>
              <div data-testid="brand-story-pillar-cities">
                <div className="font-heading text-2xl text-black">15</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-gray-500">Indian Cities Sourced</div>
              </div>
              <div data-testid="brand-story-pillar-sustainable">
                <div className="font-heading text-2xl text-black">100%</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-gray-500">Ethically Made</div>
              </div>
            </div>

            <Link
              to="/about"
              data-testid="brand-story-cta"
              className="group mt-8 inline-flex w-fit items-center gap-2 border-b border-black pb-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-black transition-colors hover:border-[#D4AF37] hover:text-[#B8912B]"
            >
              Read our story
              <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>

        {/* Gold hairline */}
        <div className="mx-auto mt-2 h-[1px] max-w-screen-lg bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
      </section>

      {/* Recently Viewed (renders only if there are items) */}
      <Suspense fallback={null}>
        <RecentlyViewed />
      </Suspense>

      {/* Newsletter / Join the community */}
      <section
        className="relative overflow-hidden bg-black text-white"
        data-testid="newsletter-section"
      >
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1503342484812-ee33283508a5?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600"
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative mx-auto max-w-screen-md px-4 py-16 text-center sm:px-6 sm:py-20">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">
            <Sparkles className="h-3 w-3" /> Join the House
          </span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl">
            Be first in line for <em className="not-italic text-gold-gradient">new drops</em>
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-white/70">
            Sign up and receive an exclusive 10% off your first order, plus early access to every collection.
          </p>
          <form
            onSubmit={submitEmail}
            className="mx-auto mt-6 flex w-full max-w-md flex-col gap-2 border border-white/20 bg-white/5 p-1 sm:flex-row sm:items-center sm:gap-0"
            data-testid="home-newsletter-form"
          >
            <div className="flex flex-1 items-center gap-2 px-3">
              <Mail className="h-4 w-4 text-white/60" />
              <input
                data-testid="home-newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full border-0 bg-transparent py-3 text-sm text-white outline-none placeholder:text-white/40"
              />
            </div>
            <button
              data-testid="home-newsletter-submit"
              type="submit"
              className="bg-[#D4AF37] px-6 py-3 text-[11px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-[#B8912B]"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
